import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import baseApi from "../../api/baseApi";

import { Slide, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const initialState = {
  announcement: {},
  announcementLoading: true,
  announcementRefresh: true,

  announcementIsDefault: [],
  announcementIsDefaultLoading: true,
  announcementIsDefaultRefresh: true,

  announcements: [],
  announcementsLoading: true,
  announcementsRefresh: true,

  createAnnouncementLoading: false,
  updateAnnouncementLoading: false,
  deleteAnnouncementLoading: false,
};

export const fetchAnnouncement = createAsyncThunk(
  "announcement/fetchAnnouncement",
  async (id) => {
    if (!id) throw new Error("Id bulunamadı!");

    try {
      const response = await baseApi.get(`/Announcement/${id}`);

      return response.data.Value;
    } catch (error) {
      throw new Error(error.response.data.errorMessage);
    }
  },
);

export const fetchAnnouncementIsDefault = createAsyncThunk(
  "announcement/fetchAnnouncementIsDefault",
  async () => {
    try {
      const response = await baseApi.get(`/Announcement/GetDefault`);

      return response.data.Value;
    } catch (error) {
      throw new Error(error.response.data.errorMessage);
    }
  },
);

export const fetchAnnouncements = createAsyncThunk(
  "announcement/fetchAnnouncements",
  async () => {
    try {
      const response = await baseApi.get(`/Announcement`);

      return response.data;
    } catch (error) {
      throw new Error(error.response.data.errorMessage);
    }
  },
);

export const createAnnouncement = createAsyncThunk(
  "announcement/createAnnouncement",
  async (values) => {
    if (!values) {
      throw new Error("Veri bulunamadı!");
    }

    const loadingToast = toast.loading("Kaydediliyor...", {
      position: "top-center",
      transition: Slide,
    });

    try {
      const formData = new FormData();
      formData.append("AnnouncementTitle", values.announcementTitle || "");
      formData.append("AnnouncementContent", values.announcementContent || "");
      formData.append("IsDefault", values.isDefault);

      if (values.announcementImage) {
        formData.append("AnnouncementImage", values.announcementImage);
      }

      const response = await baseApi.post(`/Announcement`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      toast.update(loadingToast, {
        render: response.data.result,
        type: "success",
        isLoading: false,
        autoClose: 5000,
        transition: Slide,
        hideProgressBar: true,
      });

      return response.data.result;
    } catch (error) {
      toast.update(loadingToast, {
        render: "Hata: " + error.response.data.errorMessage,
        type: "error",
        isLoading: false,
        autoClose: 5000,
        transition: Slide,
        hideProgressBar: true,
      });

      throw new Error(error.response.data.errorMessage);
    }
  },
);

export const updateAnnouncement = createAsyncThunk(
  "announcement/updateAnnouncement",
  async (values) => {
    if (!values) {
      throw new Error("Veri bulunamadı!");
    }

    const loadingToast = toast.loading("Kaydediliyor...", {
      position: "top-center",
      transition: Slide,
    });

    try {
      const formData = new FormData();
      formData.append("id", values.id);
      formData.append("announcementTitle", values.announcementTitle);
      formData.append("announcementContent", values.announcementContent);
      formData.append("isDefault", values.isDefault);

      if (values.announcementImage instanceof File) {
        formData.append("announcementImage", values.announcementImage);
      } else if (values.announcementImage === null) {
        formData.append("announcementImage", null);
      }

      const response = await baseApi.put(
        `/Announcement/${values.id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      toast.update(loadingToast, {
        render: response.data.result,
        type: "success",
        isLoading: false,
        autoClose: 5000,
        transition: Slide,
        hideProgressBar: true,
      });

      return response.data.result;
    } catch (error) {
      toast.update(loadingToast, {
        render: "Hata: " + error.response.data.errorMessage,
        type: "error",
        isLoading: false,
        autoClose: 5000,
        transition: Slide,
        hideProgressBar: true,
      });

      throw new Error(error.response.data.errorMessage);
    }
  },
);

export const deleteAnnouncement = createAsyncThunk(
  "announcement/deleteAnnouncement",
  async (id) => {
    if (!id) throw new Error("Id bulunamadı!");

    const loadingToast = toast.loading("Siliniyor...", {
      position: "top-center",
      transition: Slide,
    });

    try {
      const response = await baseApi.delete(`/Announcement/${id}`);

      toast.update(loadingToast, {
        render: "Duyuru başarıyla silindi.",
        type: "success",
        isLoading: false,
        autoClose: 5000,
        transition: Slide,
        hideProgressBar: true,
      });

      return response.data.result;
    } catch (error) {
      toast.update(loadingToast, {
        render: "Hata: " + error.response.data.errorMessage,
        type: "error",
        isLoading: false,
        autoClose: 5000,
        transition: Slide,
        hideProgressBar: true,
      });

      throw new Error(error.response.data.errorMessage);
    }
  },
);

export const announcementSlice = createSlice({
  name: "announcement",
  initialState: initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchAnnouncement.pending, (state, action) => {
        state.announcement = {};
        state.announcementLoading = true;
      })
      .addCase(fetchAnnouncement.fulfilled, (state, action) => {
        state.announcement = action.payload;
        state.announcementLoading = false;
      })
      .addCase(fetchAnnouncement.rejected, (state, action) => {
        state.announcement = {};
        state.announcementLoading = false;
      });

    builder
      .addCase(fetchAnnouncementIsDefault.pending, (state, action) => {
        state.announcementIsDefault = [];
        state.announcementIsDefaultLoading = true;
      })
      .addCase(fetchAnnouncementIsDefault.fulfilled, (state, action) => {
        state.announcementIsDefault = action.payload;
        state.announcementIsDefaultLoading = false;
      })
      .addCase(fetchAnnouncementIsDefault.rejected, (state, action) => {
        state.announcementIsDefault = [];
        state.announcementIsDefaultLoading = false;
      });

    builder
      .addCase(fetchAnnouncements.pending, (state, action) => {
        state.announcements = [];
        state.announcementsLoading = true;
      })
      .addCase(fetchAnnouncements.fulfilled, (state, action) => {
        state.announcements = action.payload;
        state.announcementsLoading = false;
      })
      .addCase(fetchAnnouncements.rejected, (state, action) => {
        state.announcements = [];
        state.announcementsLoading = false;
      });

    builder
      .addCase(createAnnouncement.pending, (state, action) => {
        state.createAnnouncementLoading = true;
      })
      .addCase(createAnnouncement.fulfilled, (state, action) => {
        state.createAnnouncementLoading = false;
        state.announcementsRefresh = !state.announcementsRefresh;
      })
      .addCase(createAnnouncement.rejected, (state, action) => {
        state.createAnnouncementLoading = false;
      });

    builder
      .addCase(updateAnnouncement.pending, (state, action) => {
        state.updateAnnouncementLoading = true;
        state.announcementsLoading = true;
      })
      .addCase(updateAnnouncement.fulfilled, (state, action) => {
        state.updateAnnouncementLoading = false;
        state.announcementsRefresh = !state.announcementsRefresh;
      })
      .addCase(updateAnnouncement.rejected, (state, action) => {
        state.updateAnnouncementLoading = false;
      });

    builder
      .addCase(deleteAnnouncement.pending, (state, action) => {
        state.deleteAnnouncementLoading = true;
      })
      .addCase(deleteAnnouncement.fulfilled, (state, action) => {
        state.deleteAnnouncementLoading = false;
        state.announcementsRefresh = !state.announcementsRefresh;
      })
      .addCase(deleteAnnouncement.rejected, (state, action) => {
        state.deleteAnnouncementLoading = false;
      });
  },
});

export default announcementSlice.reducer;
